import type { ShareLinkFeature } from '../../services/api';

/**
 * The building blocks of a 3D Map share link: what the agent reads when
 * switching one on, and what the consumer sees on the link. Kept free of
 * MUI / heavy imports — the public share site bundles it too.
 */
export interface ShareLinkFeatureInfo {
  key: ShareLinkFeature;
  label: string;
  /** Agent-facing: what switching it on gives the visitor. */
  blurb: string;
  enabled: boolean;
  /** Consumer-facing question that opens the block. */
  hook: string;
  /** Consumer-facing teaser shown in the occasional hint bubble. */
  hint: string;
  /** Consumer-facing explanation; `agent` is the agent's first name. */
  explain: (agent: string) => string;
}

export const SHARE_LINK_FEATURES: ShareLinkFeatureInfo[] = [
  {
    key: 'PRICE_PROMO_NEARBY',
    label: 'Price Promo Near You',
    blurb: 'Visitors key in a postal code and see the 5 deepest developer discounts within 5 km.',
    enabled: true,
    hook: 'Which projects near you are cutting prices?',
    hint: 'See the biggest price cuts near you',
    explain: () => 'Developers quietly lower prices on selected units. Key in any postal code — your home, office or your parents\' place — and see the 5 projects within 5 km with the biggest savings right now.',
  },
  {
    key: 'THUMBSUP_NEARBY',
    label: 'Thumbs-up Unit Near You',
    blurb: 'Visitors see the 5 projects within 5 km with the most higher-floor, lower-price units.',
    enabled: true,
    hook: 'Want a higher floor for a lower price?',
    hint: 'Higher floor, lower price — near you',
    explain: () => 'Sometimes a higher floor costs less than a lower floor of the same size. We call these thumbs-up units. Key in a postal code to see the 5 projects within 5 km that have the most of them.',
  },
  {
    key: 'VALUATION',
    label: 'Valuation',
    blurb: 'Visitors ask you to value their home: postal code → address → WhatsApp to you.',
    enabled: true,
    hook: 'What is your home worth today?',
    hint: 'Find out what your home is worth',
    explain: (agent) => `Get a free, no-obligation valuation of your current home from ${agent}. Key in your postal code, we find the address, and you send it to ${agent} on WhatsApp in one tap.`,
  },
  {
    key: 'PROJECT_REQUEST',
    label: 'Other Project Request',
    blurb: 'Visitors pick any other project they want to know about; it comes to you on WhatsApp.',
    enabled: true,
    hook: 'Looking at another project too?',
    hint: 'Ask about any other project',
    explain: (agent) => `Pick any other project you are curious about, and ${agent} will send you the details and latest prices on WhatsApp.`,
  },
  {
    key: 'SUPER_RATIO',
    label: 'Super Ratio',
    blurb: 'Value ratios across comparable launches.',
    enabled: false,
    hook: 'Is this project good value?',
    hint: 'Compare value across launches',
    explain: () => 'Value ratios across comparable launches.',
  },
];

export const SHARE_LINK_FEATURE_BY_KEY = new Map(SHARE_LINK_FEATURES.map((f) => [f.key, f]));
